import { ApiError } from '@/api/ApiError'
import {
  type MemberRow,
  normalizeEmail,
  rowFromLookup,
} from '@/features/OrgMembers/lib/memberRows'
import { fetchLookupUserIdentities } from '@/generated/http-clients/v3ApiComponents'
import { useV3ApiContext } from '@/generated/http-clients/v3ApiContext'
import { useCallback, useRef, useState } from 'react'

export const NO_MEMBER_ORG_IDS: readonly string[] = []

function pendingOwner(email: string, lookupId: number): MemberRow {
  return {
    key: normalizeEmail(email),
    email,
    role: 'viewer',
    lookupId,
    status: 'pending',
  }
}

function failedOwner(owner: MemberRow): MemberRow {
  return {
    key: owner.key,
    email: owner.email,
    role: owner.role,
    lookupId: owner.lookupId,
    status: 'lookupFailed',
  }
}

export function useOwnerLookup() {
  const { fetcherOptions } = useV3ApiContext()
  const [owner, setOwner] = useState<MemberRow | undefined>()
  const [invalidEmail, setInvalidEmail] = useState<string | undefined>()

  const ownerRef = useRef<MemberRow | undefined>(undefined)
  const nextLookupId = useRef(0)
  const latest = useRef({ fetcherOptions })
  latest.current = { fetcherOptions }

  const updateOwner = useCallback((next: MemberRow | undefined) => {
    ownerRef.current = next
    setOwner(next)
  }, [])

  const isCurrentLookup = useCallback(
    (lookupId: number) => ownerRef.current?.lookupId === lookupId,
    [],
  )

  const startLookup = useCallback(
    (email: string) => {
      nextLookupId.current += 1
      const lookupId = nextLookupId.current
      const pending = pendingOwner(email, lookupId)
      setInvalidEmail(undefined)
      updateOwner(pending)
      fetchLookupUserIdentities({
        ...latest.current.fetcherOptions,
        queryParams: { email },
      })
        .then((lookup) => {
          if (isCurrentLookup(lookupId)) {
            updateOwner(rowFromLookup(pending, lookup, NO_MEMBER_ORG_IDS))
          }
        })
        .catch((error: unknown) => {
          if (!isCurrentLookup(lookupId)) {
            return
          }
          if (error instanceof ApiError && error.status === 400) {
            updateOwner(undefined)
            setInvalidEmail(email)
            return
          }
          updateOwner(failedOwner(pending))
        })
    },
    [isCurrentLookup, updateOwner],
  )

  const clearOwner = useCallback(() => {
    nextLookupId.current += 1
    updateOwner(undefined)
    setInvalidEmail(undefined)
  }, [updateOwner])

  const lookupOwner = useCallback(
    (text: string) => {
      const email = text.trim()
      if (email === '') {
        clearOwner()
        return
      }
      const current = ownerRef.current
      if (
        current?.key === normalizeEmail(email) &&
        current.status !== 'lookupFailed'
      ) {
        return
      }
      startLookup(email)
    },
    [clearOwner, startLookup],
  )

  const retryOwnerLookup = useCallback(() => {
    const current = ownerRef.current
    if (current) {
      startLookup(current.email)
    }
  }, [startLookup])

  const selectOwnerIdentity = useCallback(
    (userId: string) => {
      const current = ownerRef.current
      if (current?.status === 'multiple') {
        updateOwner({ ...current, selectedUserId: userId })
      }
    },
    [updateOwner],
  )

  return {
    owner,
    invalidEmail,
    lookupOwner,
    clearOwner,
    retryOwnerLookup,
    selectOwnerIdentity,
  }
}
